import dynamic from 'next/dynamic'
import { BogoStickyFooterContent } from '@mc/bogo'
import { useBogo } from '@mc/bogo-utils'
import { Conditional, ShowIf } from '@mc/conditional-rendering'
import { FreeTrialStickyFooter, useFreeTrial } from '@mc/free-trial'
import { usePromotion } from '@mc/promo-utils'
import { PromoStickyFooterContent } from '@mc/promos'
import CTA from 'components/CTA'
import { trackPropsType } from 'components/CTA/events'
import { PricingText } from 'components/PricingText'
import {
  usePersonalizedStickyFooterExp,
  PersonalizedStickyFooterContent,
} from 'experiments/pen-5361_v1_personalized_sticky_footer'
import {
  CMInnovativeSwingStickyFooter,
  useCMInnovativeSwingExp,
} from 'experiments/pen-5613_v1_cm_innovative_swing'
import { useQuizEntryExperiment } from 'experiments/pen-5646_v1_quiz_entry_sticky_footer/client'
import {
  useTrackStickyFooterViewed,
  STICKY_FOOTER_TYPES,
} from './useTrackStickyFooterViewed'

interface CTAProps {
  href?: string
  trackProps: trackPropsType
  onClick: (action: string) => void
  children: React.ReactNode
  fullWidth?: boolean
  className?: string
}

const HighlightedLayout = ({ children }: { children: React.ReactNode }) => (
  <div style={{ backgroundColor: 'var(--mc-color-pink-500)' }}>{children}</div>
)

const QuizEntryMinimizedFooter = dynamic(() =>
  import('experiments/pen-5646_v1_quiz_entry_sticky_footer/client').then(
    (mod) => ({
      default: mod.QuizEntryMinimizedFooter,
    }),
  ),
)

export const StickyContent = ({ ctaProps }: { ctaProps: CTAProps }) => {
  const { isBogoOfferEligible } = useBogo()
  const { isPromotionOn } = usePromotion()
  const { trial } = useFreeTrial()

  const { isVariant: isPersonalizedStickyFooterVariant } =
    usePersonalizedStickyFooterExp()
  const { isVariant1: isCMInnovativeSwingVariant1 } = useCMInnovativeSwingExp()

  const { isQuizEntryVariant } = useQuizEntryExperiment()

  const getStickyFooterType = () => {
    if (trial.eligible) return STICKY_FOOTER_TYPES.FREE_TRIAL
    if (isPromotionOn) return STICKY_FOOTER_TYPES.PROMOTION
    if (isBogoOfferEligible) return STICKY_FOOTER_TYPES.BOGO
    if (isCMInnovativeSwingVariant1)
      return STICKY_FOOTER_TYPES.CM_INNOVATIVE_SWING_EXP
    if (isQuizEntryVariant) return STICKY_FOOTER_TYPES.QUIZ_ENTRY_EXP
    if (isPersonalizedStickyFooterVariant)
      return STICKY_FOOTER_TYPES.PERSONALIZED_EXP

    return STICKY_FOOTER_TYPES.DEFAULT
  }

  const stickyFooterType = getStickyFooterType()

  useTrackStickyFooterViewed(stickyFooterType)

  const isHighlightedFooter =
    isPromotionOn || isBogoOfferEligible || isPersonalizedStickyFooterVariant

  const renderSignUpCTA = (signUpCtaProps?: Partial<CTAProps>) => (
    <CTA
      href={ctaProps.href}
      trackProps={ctaProps.trackProps}
      isBasic
      onClick={ctaProps.onClick}
      kind={isHighlightedFooter ? 'play' : undefined}
      {...signUpCtaProps}
    >
      {ctaProps.children}
    </CTA>
  )

  return (
    <Conditional>
      <ShowIf condition={stickyFooterType === STICKY_FOOTER_TYPES.FREE_TRIAL}>
        <FreeTrialStickyFooter />
      </ShowIf>

      <ShowIf condition={stickyFooterType === STICKY_FOOTER_TYPES.PROMOTION}>
        <HighlightedLayout>
          <PromoStickyFooterContent
            SignUpCta={renderSignUpCTA}
            PricingText={() => <PricingText isStickyFooter />}
          />
        </HighlightedLayout>
      </ShowIf>

      <ShowIf condition={stickyFooterType === STICKY_FOOTER_TYPES.BOGO}>
        <HighlightedLayout>
          <BogoStickyFooterContent
            SignUpCta={renderSignUpCTA}
            PricingText={() => <PricingText isStickyFooter />}
          />
        </HighlightedLayout>
      </ShowIf>

      <ShowIf
        condition={
          stickyFooterType === STICKY_FOOTER_TYPES.CM_INNOVATIVE_SWING_EXP
        }
      >
        <CMInnovativeSwingStickyFooter trackProps={ctaProps.trackProps} />
      </ShowIf>

      <ShowIf
        condition={stickyFooterType === STICKY_FOOTER_TYPES.QUIZ_ENTRY_EXP}
      >
        <QuizEntryMinimizedFooter
          ctaHref={ctaProps.href}
          trackProps={ctaProps.trackProps}
          onClick={ctaProps.onClick}
        />
      </ShowIf>

      <ShowIf
        condition={stickyFooterType === STICKY_FOOTER_TYPES.PERSONALIZED_EXP}
      >
        <HighlightedLayout>
          <PersonalizedStickyFooterContent SignUpCta={renderSignUpCTA} />
        </HighlightedLayout>
      </ShowIf>

      <ShowIf isDefault>
        <div className='container d-flex align-items-center justify-content-end mc-py-4'>
          <p className='mc-text-h7 mc-opacity--hinted mc-mr-4'>
            <PricingText isStickyFooter />
          </p>
          {renderSignUpCTA()}
        </div>
      </ShowIf>
    </Conditional>
  )
}
